/**
 * 포트폴리오 프로젝트.
 *
 * 교육 랜딩의 카드와 상세 페이지가 같은 데이터를 쓴다. 페이지끼리는 참조할 수 없으므로
 * (FSD) 여기 엔티티에 둔다.
 *
 * 카드는 **결과 한 줄**(`outcome`)만 남기고, 상세는 그 줄에 이르기까지를 풀어 쓴다.
 * 상세가 카드의 반복이 되지 않도록, 카드에 없는 것을 담는다 — 왜 그렇게 했는지,
 * 무엇이 어려웠는지, 다음에 무엇을 다르게 할지.
 */
export type PortfolioSection = {
  readonly id: string;
  readonly heading: string;
  readonly paragraphs: readonly string[];
  /**
   * 만든 것의 기능 목록. 절 끝에 붙는다.
   *
   * 한 문단에 「지도와 코스와 이벤트와 랭킹이 있습니다」로 늘어놓으면 무엇이 몇 개인지
   * 세어 가며 읽어야 한다. 나눠 놓으면 훑는 것만으로 개수와 이름이 들어온다.
   */
  readonly features?: readonly {
    readonly name: string;
    readonly url: string;
    readonly description: string;
  }[];
  /**
   * 절 안에 끼우는 화면·자료. `before` 는 몇 번째 문단 **앞**에 놓을지다.
   *
   * 목록인 이유: 실제 화면 하나를 놓는 절도 있고, 수업 자료를 문단마다 한 장씩
   * 붙여 말과 그림을 붙여 놓는 절도 있다.
   */
  readonly media?: readonly {
    readonly kind: 'video' | 'image';
    readonly src: string;
    /**
     * 이미지 대체 텍스트 — **무엇이 찍혀 있는지**. 캡션과 같은 문장을 넣지 않는다.
     * 같으면 스크린리더와 복사한 글에서 같은 말이 두 번 나온다.
     */
    readonly alt?: string;
    readonly caption: string;
    readonly before: number;
    /**
     * 여백이 넓은 원본을 잘라 쓸 때만 준다. 없으면 원본 비율 그대로 놓는다 —
     * 발표 자료처럼 이미 정돈된 그림은 자르면 오히려 구도가 깨진다.
     */
    readonly crop?: { readonly aspect: string; readonly focus: string };
  }[];
};

export type PortfolioProject = {
  readonly id: string;
  readonly badge: string;
  readonly title: string;
  /** 상세 페이지 제목. 카드 제목이 짧을 때 풀어 쓴다 */
  readonly headline: string;
  readonly outcome: string;
  readonly caption: string;
  /** 썸네일 URL (webpack asset import 결과) */
  readonly thumb: string;
  /**
   * `cover` — 사진·키비주얼. 카드를 꽉 채운다
   * `logo` — 로고. 여백을 두고 가운데 놓는다. 꽉 채우면 로고가 잘리고 브랜드 규정에 어긋난다
   */
  readonly thumbKind: 'cover' | 'logo';
  /** 썸네일 대체 텍스트 — 무엇이 찍혀 있는지를 쓴다 */
  readonly thumbAlt: string;
  readonly sections: readonly PortfolioSection[];
  /**
   * 문의 버튼. 생략하면 화면의 기본값을 쓴다.
   *
   * 프로젝트마다 문의가 가야 할 곳이 다르다 — 지자체 건을 기업 교육 문의로 보내면
   * 접수 뒤 대응이 한 번 더 돌아간다.
   */
  readonly inquiry?: {
    readonly label: string;
    readonly to: string;
  };
};

export function findProject(
  projects: readonly PortfolioProject[],
  id: string,
): PortfolioProject | undefined {
  return projects.find((project) => project.id === id);
}
